import { type FC, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { Category } from '@/api/games';
import { ImageComponent } from '@/components/Image';
import useEventListener from '@/hooks/eventListener';
import { Winners } from '@/modules/lobby/components/StarWinners/components/Winners';
import { hasTranslation } from '@/modules/localization';
import { replaceFsCount, useButtonAction } from '@/modules/promo';
import { useGetCommonStore } from '@/store';
import { BanksLogosList } from '../../../../modules/promo/components/BanksLogosList';
import { Button, Logo } from '../../components';
import {
  DashedCopyCode,
  type TBorderStyleButton,
  type TCopyCodeButtonColor,
} from '../../components/DashedCopyCode';
import { Loader } from '../../components/Loader';
import type { LogoSize } from '../../components/Logo/styled';
import { OfferCard } from '../../components/OfferCard';
import { TopGames } from '../../components/TopGames';
import { TermsAndConditions } from '../BenefitsTemplate/components/Terms&Conds';
import { Cards } from './components/Cards';
import { Jackpot } from './components/Jackpot';
import { OfferWithLine } from './components/OfferWithLine';
import { ScBenefitsTemplate, type TBgColor } from './styled';

const MOBILE_GAMES = 2;
const DESK_GAMES = 2;

export type TButtonColor =
  | 'yellow'
  | 'green'
  | 'secondaryGreenGradient'
  | 'sandGold'
  | 'orange';

interface IBanksBenefitsTemplateProps {
  translation: string;
  bonusCode?: string;
  leftImage?: string;
  shouldShowCards?: boolean;
  fixedRegButton?: boolean;
  animatedButton?: boolean;
  isBonusWithLine?: boolean;
  fixedLogo?: boolean;
  topGames?: boolean;
  bottomTopGames?: boolean;
  latestWins?: boolean;
  mainImage: string;
  mobMainImage: string;
  bgImage: string;
  bgMobImg?: string;
  buttonColor?: TButtonColor;
  customTopGames?: string[];
  copyCodeBtnColor?: TCopyCodeButtonColor;
  validDate?: string;
  shimmerButton?: boolean;
  offerCard?: boolean;
  srcImgs?: string;
  borderCopyCodeBtn?: TBorderStyleButton;
  bgColor?: TBgColor;
  paymentsMarginTop?: number;
  fixedMobBg?: boolean;
  loopSlider?: boolean;
  transparentBgMobLogo?: boolean;
  mainImgSize?: LogoSize;
  isMobLogoMarginBottom?: boolean;
  hideJackpot?: boolean;
  deskImgTopGamesSize?: number;
  mobImgTopGamesSize?: number;
  withoutMobPadding?: boolean;
  topGamesSliderFullMobWidth?: boolean;
  slidesPerView?: number;
  centeredTopGamesSlides?: boolean;
  spaceBetweenTopGames?: number;
  hidePaymentsOnMobile?: boolean;
  hiddenTermsGrayBg?: boolean;
  mobBgHeight?: number;
  hiddenMobRegBtnBg?: boolean;
  widthRegBtn?: number;
  fontSizeRegBtnDesk?: number;
  fontSizeRegBtnMob?: number;
  isTermsOpacity?: boolean;
  offerCardThemeName?: string;
  coins?: ('coins-1' | 'coins-2' | 'coins-3')[];
  coinSizes?: Partial<
    Record<'coins-1' | 'coins-2' | 'coins-3', { width: number; height: number }>
  >;
  coinPositions?: Partial<
    Record<
      'coins-1' | 'coins-2' | 'coins-3',
      { left?: string; right?: string; top?: string; bottom?: string }
    >
  >;
  disableTopGameClick?: boolean;
  fsCount?: number;
  isShowSlider?: boolean;
  isWheelScrollTopGames?: boolean;
  defaultFsCount?: number;
}

export const BanksBenefitsTemplate: FC<IBanksBenefitsTemplateProps> = ({
  translation,
  bonusCode,
  leftImage,
  shouldShowCards,
  fixedRegButton,
  animatedButton,
  isBonusWithLine,
  fixedLogo,
  topGames,
  bottomTopGames,
  latestWins,
  mainImage,
  mobMainImage,
  bgImage,
  bgMobImg,
  buttonColor = 'yellow',
  customTopGames,
  copyCodeBtnColor,
  validDate,
  shimmerButton,
  offerCard,
  srcImgs,
  borderCopyCodeBtn,
  bgColor,
  paymentsMarginTop,
  fixedMobBg,
  loopSlider,
  transparentBgMobLogo,
  mainImgSize,
  isMobLogoMarginBottom,
  hideJackpot,
  deskImgTopGamesSize,
  mobImgTopGamesSize,
  withoutMobPadding,
  topGamesSliderFullMobWidth,
  slidesPerView,
  centeredTopGamesSlides,
  spaceBetweenTopGames,
  hidePaymentsOnMobile,
  hiddenTermsGrayBg,
  mobBgHeight,
  hiddenMobRegBtnBg,
  widthRegBtn,
  fontSizeRegBtnDesk,
  fontSizeRegBtnMob,
  isTermsOpacity,
  offerCardThemeName,
  coinPositions,
  coinSizes,
  coins,
  disableTopGameClick,
  fsCount,
  defaultFsCount,
  isShowSlider = false,
  isWheelScrollTopGames = false,
}) => {
  const { t } = useTranslation(translation);
  const [copyActive, setCopyActive] = useState(false);
  const [isTop, setIsTop] = useState(false);
  const { buttonAction, isLoading } = useButtonAction();
  const useCommonStore = useGetCommonStore();

  const isMobileDevice = useCommonStore.use.isMobileDevice();
  const { getGamesByCategory } = useCommonStore(
    useShallow((state) => ({
      getGamesByCategory: state.getGamesByCategory,
    }))
  );

  useEventListener('scroll', () => setIsTop(window.scrollY >= 20));

  const gamesPerView = isMobileDevice ? MOBILE_GAMES : DESK_GAMES;

  const shouldShowJackpot = !(
    hideJackpot ||
    topGames ||
    latestWins ||
    shouldShowCards
  );

  const shouldShowPayments = hidePaymentsOnMobile ? !isMobileDevice : true;

  useEffect(() => {
    if (!latestWins) return;
    getGamesByCategory(Category.TOP, {
      pageSize: 30,
      staticAmount: true,
    });
  }, [getGamesByCategory, latestWins]);

  const buttonColorConfig = useMemo(
    () => ({
      yellow: {
        yellow: true,
      },
      green: {
        green: true,
      },
      secondaryGreenGradient: {
        secondaryGreenGradient: true,
      },
      sandGold: {
        sandGold: true,
      },
      orange: {
        orange: true,
      },
    }),
    []
  );

  const shouldShowDefaultBonus = !isBonusWithLine;

  const isTopGamesShowSlider =
    isShowSlider || (bottomTopGames && isMobileDevice);

  const topGamesBlock = (
    <TopGames
      centeredSlides={centeredTopGamesSlides}
      customImages={customTopGames}
      deskImgSize={deskImgTopGamesSize}
      disableOnClick={disableTopGameClick}
      fullMobWidth={topGamesSliderFullMobWidth}
      isWheelScroll={isWheelScrollTopGames}
      loopSlider={loopSlider && isMobileDevice}
      mobImgSize={mobImgTopGamesSize}
      showSlider={isTopGamesShowSlider}
      slidesPerView={slidesPerView}
      spaceBetween={spaceBetweenTopGames}
      srcImgs={srcImgs}
    />
  );

  const ctaButton = useMemo(
    () => (
      <Button
        disabled={isLoading}
        {...buttonColorConfig[buttonColor]}
        animated={animatedButton}
        shimmer={shimmerButton}
        {...buttonAction}
        height="56"
        wide
      >
        {isLoading ? <Loader /> : t('joinNow')}
      </Button>
    ),
    [
      animatedButton,
      buttonAction,
      buttonColor,
      buttonColorConfig,
      isLoading,
      shimmerButton,
      t,
    ]
  );

  return (
    <ScBenefitsTemplate
      bgColor={bgColor}
      bgImage={bgImage}
      bgMobImg={bgMobImg}
      fixedMobBg={fixedMobBg}
      fixedRegButton={fixedRegButton}
      fontSizeRegBtnDesk={fontSizeRegBtnDesk}
      fontSizeRegBtnMob={fontSizeRegBtnMob}
      hiddenMobRegBtnBg={hiddenMobRegBtnBg}
      hidePaymentsOnMobile={hidePaymentsOnMobile}
      isMobLogoMarginBottom={isMobLogoMarginBottom}
      isTop={fixedLogo && isTop}
      mobBgHeight={mobBgHeight}
      mobTopGames={bottomTopGames && isMobileDevice}
      offerCard={offerCard}
      paymentsMarginTop={paymentsMarginTop}
      transparentBgMobLogo={transparentBgMobLogo}
      widthRegBtn={widthRegBtn}
      withoutMobPadding={withoutMobPadding}
    >
      {leftImage && (
        <ImageComponent
          alt="left decor"
          className="left-img"
          height={300}
          src={leftImage}
          width={200}
        />
      )}

      <div className="container">
        {offerCard && <Logo />}
        <div className="top-section">
          <div className="left-column">
            {!offerCard && <Logo />}
            <ImageComponent
              alt="mobile main img"
              className="mob-img"
              height={mainImgSize ? Number(mainImgSize?.heightMob) : 122}
              src={mobMainImage}
              width={mainImgSize ? Number(mainImgSize?.widthMob) : 344}
            />
            {shouldShowDefaultBonus && (
              <div className="bonus">
                {hasTranslation(t, 'bonus.title') && (
                  <p className="title">
                    <Trans i18nKey="bonus.title" ns={translation} />
                  </p>
                )}

                {hasTranslation(t, 'bonus.count') && (
                  <p className="fs-count">
                    {replaceFsCount(t('bonus.count'), fsCount)}
                  </p>
                )}
                {hasTranslation(t, 'bonus.medium-count') && (
                  <p className="fs-count medium-count">
                    {t('bonus.medium-count')}
                  </p>
                )}
                {hasTranslation(t, 'bonus.offer') && (
                  <p className="fs">
                    <Trans i18nKey="bonus.offer" ns={translation} />
                  </p>
                )}
                {hasTranslation(t, 'bonus.fs') && (
                  <p className="fs">{t('bonus.fs')}</p>
                )}
                {hasTranslation(t, 'bonus.description') && (
                  <p className="title">{t('bonus.description')}</p>
                )}
              </div>
            )}
            {isBonusWithLine && (
              <OfferWithLine fsCount={fsCount} translation={translation} />
            )}
            {offerCard && srcImgs && (
              <OfferCard
                bonusCode={bonusCode}
                borderCopyCodeBtn={borderCopyCodeBtn}
                buttonColor={buttonColor}
                coinPositions={
                  coinPositions || {
                    'coins-1': { left: '-45px', top: '230px' },
                  }
                }
                coinSizes={coinSizes}
                coins={coins}
                copyCodeBtnColor={copyCodeBtnColor}
                defaultFsCount={defaultFsCount}
                fsCount={fsCount}
                srcImgs={srcImgs}
                themeName={offerCardThemeName}
                translation={translation}
                validDate={validDate}
              />
            )}
            {bonusCode && !offerCard && (
              <div className="code-wrapper">
                <DashedCopyCode
                  code={bonusCode}
                  copyActive={copyActive}
                  setCopyActive={setCopyActive}
                />
              </div>
            )}
            {(!offerCard || (offerCard && isMobileDevice)) && (
              <>
                <div className="desktop-wrapper">{ctaButton}</div>
                {!offerCard && (
                  <TermsAndConditions
                    grayBg={!hiddenTermsGrayBg}
                    isTitleBtnOpacity={isTermsOpacity}
                    translation={translation}
                    validDate={validDate}
                  />
                )}
              </>
            )}
          </div>
          <div className="right-column">
            <ImageComponent
              alt="main img"
              className="main-img"
              height={mainImgSize ? Number(mainImgSize?.height) : 300}
              src={mainImage}
              width={mainImgSize ? Number(mainImgSize?.width) : 576}
            />
            {bottomTopGames && isMobileDevice && topGamesBlock}
            {shouldShowCards && <Cards translation={translation} />}
            {shouldShowJackpot && <Jackpot />}
            {topGames && topGamesBlock}
            {latestWins && (
              <div className="winners-wrapper">
                <Winners
                  itemsPerView={gamesPerView}
                  title={t('latestWins')}
                  {...buttonAction}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {bottomTopGames && !isMobileDevice && (
        <div className="bottom-top-games">{topGamesBlock}</div>
      )}
      {shouldShowPayments && (
        <div className="payments-wrapper">
          <BanksLogosList bgColor={bgColor} />
        </div>
      )}
      {fixedRegButton && <div className="mobile-wrapper">{ctaButton}</div>}
    </ScBenefitsTemplate>
  );
};
